import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { BuilderMessage } from '@/utils/api-pb';

interface ChatPanelProps {
  messages: BuilderMessage[];
  isSending: boolean;
  /** Chat is unavailable until the page has a first version. */
  disabled?: boolean;
  onSend: (message: string) => void;
  /**
   * Translation prefix for the wording that differs between a landing page and a deck
   * ("what to change on the page" against "on the slides"). The panel's own furniture —
   * its heading, the send button, the thinking line — stays shared: it is the same
   * panel doing the same job either way.
   */
  i18nPrefix?: string;
  /** Suggestion keys under `<prefix>.suggestions`. */
  suggestionKeys?: readonly string[];
}

/** Shown once, to make the first move obvious instead of leaving an empty box. */
const SUGGESTION_KEYS = ['shorter', 'tone', 'addFaq', 'darker'] as const;

export function ChatPanel({
  messages,
  isSending,
  disabled,
  onSend,
  i18nPrefix = 'chat',
  suggestionKeys = SUGGESTION_KEYS,
}: ChatPanelProps) {
  const { t } = useTranslation('builder');
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the newest message in view as the conversation grows.
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages.length, isSending]);

  function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || isSending || disabled) return;
    onSend(trimmed);
    setDraft('');
  }

  return (
    <aside className="flex h-[min(40vh,360px)] min-h-[280px] w-full flex-col gap-[12px] rounded-[16px] border border-[var(--grey-accent,#eeeeee)] p-[16px] lg:h-full lg:min-h-0 lg:w-[340px] lg:shrink-0">
      <h2 className="font-manrope text-[15px] font-bold text-[color:var(--black-text,#0d0d0d)]">
        {t('chat.title')}
      </h2>

      <div ref={listRef} className="flex min-h-0 flex-1 flex-col gap-[10px] overflow-y-auto">
        {messages.length === 0 ? (
          <div className="flex flex-col gap-[10px]">
            <p className="font-segoe text-[14px] text-[color:var(--grey-text,#565858)]">
              {t(`${i18nPrefix}.empty`)}
            </p>
            <div className="flex flex-wrap gap-[6px]">
              {suggestionKeys.map((key) => (
                <Button
                  key={key}
                  type="button"
                  variant="brand-outline"
                  size="brand-sm"
                  disabled={disabled || isSending}
                  onClick={() => send(t(`${i18nPrefix}.suggestions.${key}`))}
                  className="h-auto whitespace-normal rounded-[16px] px-[10px] py-[5px] text-left text-[13px]"
                >
                  {t(`${i18nPrefix}.suggestions.${key}`)}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={
                'max-w-[92%] rounded-[16px] px-[12px] py-[8px] font-segoe text-[14px] leading-[20px] ' +
                (message.role === 'user'
                  ? 'self-end bg-[var(--main-brand,#2197f1)] text-white'
                  : 'self-start bg-[var(--grey-background,#f8f8f8)] text-[color:var(--black-text,#0d0d0d)]')
              }
            >
              <p className="whitespace-pre-line">{message.content}</p>
              {/* Only shown when the reply actually changed the page, so "no version"
                  reads as "nothing was touched" rather than as a missing label. */}
              {message.role === 'assistant' && message.version !== null && (
                <span className="mt-[4px] block font-segoe text-[12px] text-[color:var(--grey-text,#969696)]">
                  {t('editor.version', { n: message.version })}
                </span>
              )}
            </div>
          ))
        )}

        {isSending && (
          <div className="self-start rounded-[16px] bg-[var(--grey-background,#f8f8f8)] px-[12px] py-[8px] font-segoe text-[14px] text-[color:var(--grey-text,#565858)]">
            {t('chat.thinking')}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-[8px]">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter breaks the line — the usual chat contract.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send(draft);
            }
          }}
          placeholder={disabled ? t(`${i18nPrefix}.disabled`) : t(`${i18nPrefix}.placeholder`)}
          rows={3}
          maxLength={2000}
          disabled={disabled || isSending}
          className="rounded-xl border-[#eeeeee] text-[14px] resize-none"
        />
        <Button
          variant="brand"
          size="brand"
          onClick={() => send(draft)}
          disabled={disabled || isSending || !draft.trim()}
        >
          {t('chat.send')}
        </Button>
      </div>
    </aside>
  );
}
